import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { onboardingAPI } from "../services/api";
import { Button } from "./ds";
import { AuthLayout, StatusPanel } from "./auth/AuthLayout";

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const exchanged = useRef(false);

  const [status, setStatus] = useState("loading"); // loading, success, error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    // Get params from URL (works for both old Strava flow and new Google SSO flow)
    const userParam = searchParams.get("user");
    const errorParam = searchParams.get("error");
    // Note: token is set as HTTP-only cookie by backend, we don't need to read it

    // Check for error from backend
    if (errorParam) {
      console.error("Auth error from backend:", errorParam);
      setStatus("error");
      setErrorMessage(decodeURIComponent(errorParam));
      return;
    }

    // Check for user data (both flows send user data in URL)
    if (userParam) {
      try {
        const userData = JSON.parse(decodeURIComponent(userParam));

        // Save user data to context + localStorage
        login(userData);
        setStatus("success");

        // Check if the user needs onboarding and go straight there — avoids
        // a dashboard→onboarding bounce (Layout would redirect anyway, but
        // this skips the round trip).
        const nextRoute = async () => {
          try {
            const status = await onboardingAPI.getStatus();
            return status.onboarding_completed ? "/dashboard" : "/onboarding";
          } catch (error) {
            console.error("Failed to check onboarding status:", error);
            return "/dashboard";
          }
        };

        // Redirect after brief delay to show success state
        setTimeout(async () => {
          const route = await nextRoute();
          navigate(route, { replace: true });
        }, 1500);
      } catch (err) {
        console.error("Failed to parse user data:", err);
        setStatus("error");
        setErrorMessage("Failed to parse authentication data");
      }
      return;
    }

    // No user data received - something went wrong
    setStatus("error");
    setErrorMessage("No authentication data received");
  }, [searchParams, login, navigate]);

  return (
    <AuthLayout>
      {status === "loading" && (
        <StatusPanel icon="spinner" title="Signing you in" body="Connecting your Google account." />
      )}
      {status === "success" && (
        <StatusPanel icon="check" title="You’re in" body="Taking you to your training…" />
      )}
      {status === "error" && (
        <StatusPanel icon="x" title="That didn’t connect"
          body="Google didn’t send us back a sign-in. Try again, or use your email and password."
          footnote={errorMessage || null}>
          <Button block onClick={() => navigate('/login')}>Back to sign in</Button>
        </StatusPanel>
      )}
    </AuthLayout>
  );
};

export default AuthCallback;
